import { Badge, Group, Select, Stack, Text, Textarea } from "@mantine/core"
import {
  CancelButton,
  DeleteButtonWithConfirmModal,
  SubmitButton,
} from "@components/Buttons"
import { Form } from "@components/typed-form"
import { SuspenseWithErrorBoundary } from "@components/suspense"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useTranslation } from "react-i18next"
import { DefaultError } from "@components/error-context"
import {
  hasValidationErrors,
  tryGetValidationErrorsAsMantineErrors,
} from "@/utils/to-mantine-validation-errors"
import { DateOnly, Nullable, TimeOnly } from "@/@client/System"
import { DatePickerInput, TimeInput } from "@mantine/dates"
import { mapNullable } from "@/utils/nullable"
import { PersonStatusDto } from "@/@client/Theasoft_ApiServer_Orchestra"
import { usePostMutation } from "@/typed-client/hooks/use-post-mutation"
import { useSuspenseQueryGet } from "@/typed-client/hooks/use-suspense-query-get"
import { RenderSimplePerson } from "@/blocks/persons-and-users/User"
import { dateToDateOnly } from "@/utils/date-to-date-only"
import { RenderShiftType } from "@/blocks/shift-types/render-shift-type"
import { useRequestableAbsenceShiftTypes } from "../../../../../blocks/shift-types/useRequestableAbsenceShiftTypes"
import { ts_Schicht } from "../../../../../@client/Theasoft_DataClasses_Perso"
import { match } from "ts-pattern"
import { useBatchedShiftTypes } from "../../../../../-dataloaders/shift-types"
import { timeOnlyToMantineTime } from "../../../../../utils/time-only-to-mantine-time"
import { mantineTimeToTimeOnly } from "../../../../../utils/mantine-time-to-time-only"

export type EditAbsence = {
  type: "absence-cell"
  personStatus: PersonStatusDto
  contractId: number
  personId: number
  shiftTypeId: number
  date: DateOnly
}

export function EditAbsenceShiftModalContent({
  departmentId,
  dutyRosterId,
  shiftId,
  onCancel,
  contractId,
  date,
  onEdited,
}: {
  departmentId: number
  contractId: number
  date: DateOnly
  dutyRosterId: number
  shiftId: ts_Schicht
  onCancel: () => void
  onEdited?: () => void
}) {
  const { t } = useTranslation("app_server")
  const { data: availableAbsenceShiftTypes } = useSuspenseQueryGet(
    "/api/ShiftType/GetRequestableAbsenceShiftTypesForDepartment",
    { departmentId }
  )

  const { data: shift } = useSuspenseQueryGet(
    "/api/DepartmentDutyRoster/GetShift",
    {
      dutyRosterId,
      shiftId,
    }
  )
  const rawShiftId = match(shiftId)
    .with("Schicht1", () => 1)
    .with("Schicht2", () => 2)
    .with("Schicht3", () => 3)
    .exhaustive()
  const { data: shiftType } = useBatchedShiftTypes(shift.shiftTypeId)

  if (!shift) {
    return null
  }

  const readOnly = !availableAbsenceShiftTypes.some(
    (st) => st.shiftTypeId === shift.shiftTypeId
  )

  return (
    <Form
      key={JSON.stringify(shift)}
      url="/api/DepartmentDutyRoster/EditShift"
      onSuccess={() => {
        onEdited && onEdited()
      }}
      initialValues={{
        startTime: mapNullable(shift.startTime, timeOnlyToMantineTime),
        endTime: mapNullable(shift.endTime, timeOnlyToMantineTime),
        note: shift.shiftNote,
        shiftTypeId: shift.shiftTypeId,
      }}
      transform={(x) => ({
        contractId,
        date,
        dutyRosterId,
        shiftId: rawShiftId,
        shiftTypeId: x.shiftTypeId,
        shiftNote: x.note,
        startTime: mapNullable(x.startTime, mantineTimeToTimeOnly),
        endTime: mapNullable(x.endTime, mantineTimeToTimeOnly),
      })}
    >
      {(f, _) => (
        <>
          <Stack>
            <Badge>
              {t("Type")} {shiftType?.name}
            </Badge>
            <Group>
              <TimeInput
                readOnly={readOnly}
                {...f.getInputProps(_.startTime._PATH_)}
                label={t("From")}
              />
              <TimeInput
                readOnly={readOnly}
                {...f.getInputProps(_.endTime._PATH_)}
                label={t("Until")}
              />
            </Group>
            <Textarea
              readOnly={readOnly}
              {...f.getInputProps(_.note._PATH_)}
              label={t("Note")}
            />
            <Group justify="end">
              <CancelButton onMouseDown={() => onCancel()} />
              <SubmitButton disabled={readOnly}>
                {t("EditAbsence")}
              </SubmitButton>
            </Group>
          </Stack>
        </>
      )}
    </Form>
  )
}
